import { getGetCurrentUserQueryKey, useSetTermsAndConditionsConsent } from "@bvhi/backend-api/apis";
import { Box, Button, Link, Stack, Typography } from "@bvhi/ui-base";
import { useQueryClient } from "@tanstack/react-query";
import { Trans, useTranslation } from "react-i18next";
import { getAppConfig } from "../config/config";
import { useToast } from "../hooks/use-toast";
import type { ReactNode } from "react";

export interface Agb2026ConsentGateProps {
  onContinueWithoutConsent?: () => void;
}

const Bold = ({ children }: { children?: ReactNode }) => (
  <Typography component="span" fontWeight={600}>
    {children}
  </Typography>
);

export function UpdatedTermsOfServiceConsentGate({ onContinueWithoutConsent }: Agb2026ConsentGateProps) {
  const { t } = useTranslation(["terms-of-service2026"]);
  const queryClient = useQueryClient();
  const { pushCustomSuccessToast, pushCustomErrorToast } = useToast();
  const consentDeadline = getAppConfig().env.updateAGBConsentDeadline?.trim() || "";
  const showContinueWithout = !consentDeadline || new Date() < new Date(consentDeadline);

  const { mutate: setConsent, isPending } = useSetTermsAndConditionsConsent({
    mutation: {
      onSuccess: () => {
        pushCustomSuccessToast("Zustimmung zu den neuen AGB wurde gespeichert.");
        void queryClient.invalidateQueries({ queryKey: getGetCurrentUserQueryKey() });
      },
      onError: () => {
        pushCustomErrorToast("Fehler beim Speichern der Zustimmung.");
      },
    },
  });

  const handleAgree = () => {
    setConsent({
      data: { termsAndConditionsConsent: new Date().toISOString() },
    });
  };

  return (
    <Box
      sx={{
        position: "fixed",
        inset: 0,
        zIndex: 1300,
        bgcolor: "background.paper",
        overflow: "auto",
        p: 3,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
      }}
    >
      <Stack maxWidth="720px" spacing={2} sx={{ width: "100%" }}>
        <Typography fontWeight={600} textAlign="center" variant="h6">
          {t("agb2026.consent.title")}
        </Typography>
        <Typography>{t("agb2026.consent.subtitle")}</Typography>
        <Typography fontWeight={600} textAlign="center" variant="subtitle1">
          {t("agb2026.consent.headline")}
        </Typography>
        <Typography component="span">
          <Trans components={[<Bold key="0" />, <Bold key="1" />]} i18nKey="agb2026.consent.body1" ns="terms-of-service2026" />
        </Typography>
        <Box component="ul" sx={{ pl: 2, my: 0 }}>
          <li>
            <Typography component="span">
              <Trans components={[<Bold key="0" />, <Bold key="1" />]} i18nKey="agb2026.consent.bullet1" ns="terms-of-service2026" />
            </Typography>
          </li>
          <li>
            <Typography component="span">
              <Trans components={[<Bold key="0" />, <Bold key="1" />]} i18nKey="agb2026.consent.bullet2" ns="terms-of-service2026" />
            </Typography>
          </li>
        </Box>
        <Typography>{t("agb2026.consent.body2")}</Typography>
        <Box
          sx={{
            border: "2px solid",
            borderColor: "error.main",
            borderRadius: 1,
            p: 2,
          }}
        >
          <Typography color="error" fontSize="1.1rem" fontWeight={700} sx={{ mb: 1 }} textAlign="center">
            {t("agb2026.consent.warning")}
          </Typography>
          <Typography>
            <Trans
              i18nKey="agb2026.consent.warning.body"
              ns="terms-of-service2026"
              components={{
                0: <Typography color="error.main" component="span" fontWeight={600} />,
                1: <Typography component="span" fontWeight={600} />,
              }}
            />
          </Typography>
        </Box>
        <Typography>
          {t("agb2026.consent.link.prefix")}
          <Link
            href="/terms-of-service"
            rel="noopener noreferrer"
            sx={{ color: "primary.main", fontWeight: 600, textDecoration: "underline" }}
            target="_blank"
          >
            {t("agb2026.consent.link.word")}
          </Link>
          {t("agb2026.consent.link.suffix")}
        </Typography>
        <Box sx={{ borderTop: 1, borderColor: "divider", pt: 2 }}>
          <Stack direction="row" flexWrap="wrap" spacing={2}>
            <Button disabled={isPending} onClick={handleAgree} variant="contained">
              {t("agb2026.consent.button.agree")}
            </Button>
            {showContinueWithout && (
              <Button disabled={isPending} onClick={onContinueWithoutConsent} variant="outlined">
                {t("agb2026.consent.button.continueWithout")}
              </Button>
            )}
          </Stack>
        </Box>
        <Box sx={{ borderTop: 1, borderColor: "divider", pt: 2 }}>
          <Typography color="text.secondary" variant="body2">
            {t("agb2026.consent.footer.prefix")}
            <Link
              href="https://bvhi.org/hidatasync"
              rel="noopener noreferrer"
              sx={{ color: "primary.main", textDecoration: "underline", fontSize: "inherit" }}
              target="_blank"
            >
              {t("agb2026.consent.footer.link")}
            </Link>
            {t("agb2026.consent.footer.suffix")}
          </Typography>
          <Link
            href="mailto:support-hidatasync@adesso.de"
            sx={{ color: "primary.main", textDecoration: "underline", display: "block", mt: 0.5, fontSize: 14 }}
          >
            {t("agb2026.consent.footer.email")}
          </Link>
          <Typography variant="body2">{t("agb2026.consent.footer.phone")}</Typography>
        </Box>
      </Stack>
    </Box>
  );
}
